import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import * as mobxReact from 'mobx-react';
import DatePicker from 'react-datepicker';
import { max, min } from 'date-fns';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import './date-sort-filter-dropdown.scss';
import Text from '../text/text';
import DefaultButton from '../default-button/default-button';
import { meetingsSortOrdersLabels, SORT_MEETINGS_TEXT } from '../../../constants/meeting.constants';
import { OERadioSingleOption } from '../oe-radio-options/oe-radio-single-option';
import Arrow from '../../../images/svg/icons8-expand-arrow';
import { SortOrder } from '../../../api/shared.dto';
import { DATE_TEXT, LOADING, NO_MEETINGS_FOUND } from '../../../constants/common-labels.constants';
import CustomDatePickerHeader, { DatePickerHeaderProps } from '../../_events/_manage-event/shared/custom-date-picker-header/custom-date-picker-header';

export interface IProps {
    hasSelectedDateRange: boolean;
    onSelectDates: (dates: Date[]) => void;
    firstSelectedDate: Date | null;
    lastSelectedDate: Date | null;
    fetchAvailableDates: () => void;
    availableDatesList: Date[] | null;
    isMeetingsDatesLoading: boolean;
    onApply: (sortOrder: SortOrder) => void;
    onReset: () => void;
    selectedSortOrder: SortOrder;
    selectedFilterDays: Date [];
    intl: IntlShape;
}

const DateSortFilterDropdown = (props: IProps) => {
    const {
        availableDatesList,
        onApply,
        intl,
        selectedSortOrder,
        firstSelectedDate,
        lastSelectedDate,
        fetchAvailableDates,
        onSelectDates,
        selectedFilterDays,
        hasSelectedDateRange,
        isMeetingsDatesLoading,
        onReset
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [sortOpt, setSortOpt] = useState<SortOrder>(selectedSortOrder);

    useEffect(() => {
        setSortOpt(selectedSortOrder);
    }, [selectedSortOrder]);

    const onDropdownClick = () => {
        if (!isOpen) fetchAvailableDates();
        setIsOpen(!isOpen);
        setSortOpt(selectedSortOrder);
        onSelectDates(selectedFilterDays);
    };

    const handleApply = () => {
        setIsOpen(false);
        onApply(sortOpt);
    };

    const sortOptionsLabels = meetingsSortOrdersLabels(intl);
    const minDate = min(availableDatesList);
    const maxDate = max(availableDatesList);

    return (
        <div className='date-selection-dropdown multi-selection-dropdown'>
            <div
                onClick={onDropdownClick}
                className={classnames('cell-header-btn', {
                    'cell-header-btn--active': isOpen || hasSelectedDateRange
                })}
            >
                <Text message={intl.formatMessage({...DATE_TEXT})}
                      withEllipsis
                />
                <div className={`${selectedSortOrder} multi-selection-dropdown__sort-direction`}>
                    <Arrow />
                </div>
            </div>

            {isOpen &&
                <>
                    <div className='multi-selection-dropdown__overlay' onClick={() => setIsOpen(false)}/>
                    <div className='multi-selection-dropdown__options'>
                        {availableDatesList.length ? (
                            <>
                                <div
                                    className='multi-selection-dropdown__content'
                                >
                                    <div className='multi-selection-dropdown__sort'>
                                        <div className='multi-selection-dropdown__sort-section-title'>
                                            {
                                                intl.formatMessage({...SORT_MEETINGS_TEXT})
                                            }
                                        </div>
                                        <div className='multi-selection-dropdown__sort-items-wrapper'>
                                            {
                                                Object.values(SortOrder).map(option => {
                                                    return <div
                                                        key={option}
                                                        className={`multi-selection-dropdown__sort-item ${option}`}
                                                    >
                                                        <OERadioSingleOption
                                                            option={{ label: sortOptionsLabels[option] }}
                                                            value={selectedSortOrder}
                                                            checked={option === sortOpt}
                                                            onToggle={() => setSortOpt(option as SortOrder)}
                                                        />
                                                        <Arrow />
                                                    </div>;
                                                })
                                            }
                                        </div>
                                    </div>
                                    <div className='multi-selection-dropdown__date-calendar'>
                                        <DatePicker
                                            renderCustomHeader={(headerProps: DatePickerHeaderProps) => (
                                                <CustomDatePickerHeader
                                                    {...headerProps}
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                />
                                            )}
                                            selected={firstSelectedDate}
                                            onChange={onSelectDates}
                                            startDate={firstSelectedDate}
                                            endDate={lastSelectedDate}
                                            monthsShown={2}
                                            minDate={minDate}
                                            maxDate={maxDate}
                                            includeDates={availableDatesList}
                                            selectsRange
                                            inline
                                            disabledKeyboardNavigation
                                        />
                                    </div>
                                </div>
                                <div className='multi-selection-dropdown__button-bar'>
                                    <DefaultButton
                                        modifier='cancel'
                                        text='Reset'
                                        onClick={onReset}
                                        className='read-only-textarea__controls-button'
                                        disabled={!firstSelectedDate}
                                    />
                                    <DefaultButton
                                        modifier='confirm-safe'
                                        text='Apply'
                                        onClick={handleApply}
                                        className='read-only-textarea__controls-button'
                                    />
                                </div>
                            </>
                        ) : (
                            <div className='date-selection-dropdown__placeholder'>
                                {isMeetingsDatesLoading ? (
                                    <FormattedMessage
                                        {...LOADING}
                                        values={{
                                            suffix: '...'
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage{...NO_MEETINGS_FOUND}/>
                                )}
                            </div>
                        )}
                    </div>
                </>
            }
        </div>
    );
};

export default mobxReact.observer(injectIntl(DateSortFilterDropdown));
